// ./src/contexts/ActiveParkingContext/ActiveParkingContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { DEFAULT_PAGE_SIZE } from "@/config";

import {
  ParkingSession,
  ParkingSessionInput,
  ParkingSessionService,
  PARKING_SESSION_STATUS,
  ParkingSessionEndProps,
} from "@/utils/api/ParkingSessionService";

import { useApp } from "@/contexts/AppContext";

// ============================================
// Constants
// ============================================

/**
 * Default refresh interval in milliseconds (60 seconds)
 * Can be overridden via VITE_ACTIVE_PARKING_REFRESH_INTERVAL env var
 */
const DEFAULT_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Get refresh interval from environment or use default
 */
const getDefaultRefreshInterval = (): number => {
  const envValue = import.meta.env.VITE_ACTIVE_PARKING_REFRESH_INTERVAL;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_REFRESH_INTERVAL_MS;
};

// ============================================
// Types
// ============================================

export interface ActiveParkingContextValue {
  // State
  activeSessions: ParkingSession[];
  loading: boolean;

  // Computed
  hasActiveSessions: boolean;
  activeSessionCount: number;

  // Lookup
  activeSessionForCar: (carId: string) => ParkingSession | undefined;
  isCarParked: (carId: string) => boolean;

  // Operations
  startParking: (params: ParkingSessionInput) => Promise<ParkingSession | null>;
  updateParking: (id: string, params: ParkingSessionInput) => Promise<ParkingSession | null>;
  endParking: (
    id: string,
    params: ParkingSessionEndProps
  ) => Promise<ParkingSession | null>;

  // Operation states
  isStarting: boolean;
  isUpdating: boolean;
  isEnding: boolean;

  // Refresh/reload
  refresh: () => Promise<void>;

  // Manual state management
  setActiveSessions: (sessions: ParkingSession[]) => void;
  clearActiveSessions: () => void;

  // Error handling
  lastError: Error | null;
  clearError: () => void;

  // Refresh interval info
  refreshIntervalMs: number;
  isRefreshPaused: boolean;
}

export interface ActiveParkingProviderProps {
  children: React.ReactNode;

  /** Whether to auto-load active parking sessions on mount (default: true) */
  autoLoad?: boolean;

  /**
   * Refresh interval in milliseconds (default: 60000 = 1 minute)
   * Set to 0 or null to disable auto-refresh
   * Can also be set via VITE_ACTIVE_PARKING_REFRESH_INTERVAL env var
   */
  refreshIntervalMs?: number | null;

  /**
   * Whether to pause refresh when tab/window is not visible (default: true)
   */
  pauseWhenHidden?: boolean;

  /**
   * Whether to refresh immediately when tab becomes visible again (default: true)
   * Only applies when pauseWhenHidden is true
   */
  refreshOnVisible?: boolean;
}

// ============================================
// Context
// ============================================

const ActiveParkingContext = createContext<ActiveParkingContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

export const ActiveParkingProvider: React.FC<ActiveParkingProviderProps> = ({
  children,
  autoLoad = true,
  refreshIntervalMs: propRefreshInterval,
  pauseWhenHidden = true,
  refreshOnVisible = true,
}) => {
  const { user, isAuthenticated } = useApp();

  // Determine refresh interval (prop > env > default)
  const refreshIntervalMs = useMemo(() => {
    if (propRefreshInterval === null || propRefreshInterval === 0) {
      return 0; // Disabled
    }
    if (propRefreshInterval !== undefined && propRefreshInterval > 0) {
      return propRefreshInterval;
    }
    return getDefaultRefreshInterval();
  }, [propRefreshInterval]);

  // State
  const [activeSessions, setActiveSessionsState] = useState<ParkingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Refs for interval management
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastRefreshRef = useRef<number>(0);

  // ============================================
  // Computed values
  // ============================================

  const hasActiveSessions = useMemo(
    () => activeSessions.length > 0,
    [activeSessions]
  );

  const activeSessionCount = useMemo(
    () => activeSessions.length,
    [activeSessions]
  );

  const isRefreshPaused = useMemo(
    () => pauseWhenHidden && !isTabVisible,
    [pauseWhenHidden, isTabVisible]
  );

  // ============================================
  // Lookup helpers
  // ============================================

  const activeSessionForCar = useCallback(
    (carId: string): ParkingSession | undefined => {
      return activeSessions.find((s) => s.carId === carId);
    },
    [activeSessions]
  );

  const isCarParked = useCallback(
    (carId: string): boolean => {
      return activeSessions.some((s) => s.carId === carId);
    },
    [activeSessions]
  );

  // ============================================
  // Load active parking sessions
  // ============================================

  const loadActiveSessions = useCallback(
    async (isBackgroundRefresh = false): Promise<void> => {
      if (!isAuthenticated || !user?.id) {
        setActiveSessionsState([]);
        return;
      }

      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setLastError(null);

      try {
        const result = await ParkingSessionService.list(
          { status: [PARKING_SESSION_STATUS.ACTIVE] },
          { page: 1, pageSize: DEFAULT_PAGE_SIZE },
          [{ name: "startTime", order: "DESC" }]
        );

        if (result.code === OP_RESULT_CODES.OK) {
          const sessions = (result.data as ParkingSession[]) || [];
          setActiveSessionsState(sessions);
        } else {
          setActiveSessionsState([]);
          console.error("Failed to load active parking sessions");
        }

        lastRefreshRef.current = Date.now();
      } catch (error) {
        console.error("Error loading active parking sessions:", error);
        setLastError(
          error instanceof Error
            ? error
            : new Error("Failed to load active parking sessions")
        );
        setActiveSessionsState([]);
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
        setHasLoaded(true);
      }
    },
    [isAuthenticated, user?.id]
  );

  // ============================================
  // Start a parking session
  // ============================================

  const startParking = useCallback(
    async (params: ParkingSessionInput): Promise<ParkingSession | null> => {
      if (!isAuthenticated) return null;

      // Prevent starting if this car already has an active session
      if (params.carId && activeSessions.some((s) => s.carId === params.carId)) {
        setLastError(
          new Error("This vehicle already has an active parking session")
        );
        return null;
      }

      setIsStarting(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.start(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          setActiveSessionsState((prev) => [newSession, ...prev]);
          return newSession;
        } else {
          setLastError(new Error("Failed to start parking session"));
          return null;
        }
      } catch (error) {
        console.error("Error starting parking session:", error);
        setLastError(
          error instanceof Error
            ? error
            : new Error("Failed to start parking session")
        );
        return null;
      } finally {
        setIsStarting(false);
      }
    },
    [isAuthenticated, activeSessions]
  );

  // ============================================
  // Update a parking session
  // ============================================

  const updateParking = useCallback(
    async (
      id: string,
      params: ParkingSessionInput
    ): Promise<ParkingSession | null> => {
      if (!isAuthenticated) return null;

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          // Update in local state if still active
          if (updatedSession.status === PARKING_SESSION_STATUS.ACTIVE) {
            setActiveSessionsState((prev) =>
              prev.map((s) => (s.id === id ? updatedSession : s))
            );
          } else {
            // Session is no longer active, remove from active list
            setActiveSessionsState((prev) => prev.filter((s) => s.id !== id));
          }

          return updatedSession;
        } else {
          setLastError(new Error("Failed to update parking session"));
          return null;
        }
      } catch (error) {
        console.error("Error updating parking session:", error);
        setLastError(
          error instanceof Error
            ? error
            : new Error("Failed to update parking session")
        );
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [isAuthenticated]
  );

  // ============================================
  // End a parking session
  // ============================================

  const endParking = useCallback(
    async (
      id: string,
      params: ParkingSessionEndProps
    ): Promise<ParkingSession | null> => {
      if (!isAuthenticated) return null;

      setIsEnding(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.end(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const endedSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          // Remove from active sessions
          setActiveSessionsState((prev) => prev.filter((s) => s.id !== id));

          return endedSession;
        } else {
          setLastError(new Error("Failed to end parking session"));
          return null;
        }
      } catch (error) {
        console.error("Error ending parking session:", error);
        setLastError(
          error instanceof Error
            ? error
            : new Error("Failed to end parking session")
        );
        return null;
      } finally {
        setIsEnding(false);
      }
    },
    [isAuthenticated]
  );

  // ============================================
  // Manual state management
  // ============================================

  const setActiveSessions = useCallback((sessions: ParkingSession[]) => {
    setActiveSessionsState(sessions);
  }, []);

  const clearActiveSessions = useCallback(() => {
    setActiveSessionsState([]);
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await loadActiveSessions(false);
  }, [loadActiveSessions]);

  // ============================================
  // Visibility change handler
  // ============================================

  useEffect(() => {
    if (!pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);

      if (isVisible && refreshOnVisible && isAuthenticated && hasLoaded) {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        const minTimeBetweenRefreshes =
          refreshIntervalMs > 0 ? refreshIntervalMs / 2 : 30000;

        if (timeSinceLastRefresh >= minTimeBetweenRefreshes) {
          loadActiveSessions(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    pauseWhenHidden,
    refreshOnVisible,
    refreshIntervalMs,
    isAuthenticated,
    hasLoaded,
    loadActiveSessions,
  ]);

  // ============================================
  // Auto-refresh interval
  // ============================================

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (
      refreshIntervalMs <= 0 ||
      !isAuthenticated ||
      !hasLoaded ||
      (pauseWhenHidden && !isTabVisible)
    ) {
      return;
    }

    intervalRef.current = setInterval(() => {
      loadActiveSessions(true);
    }, refreshIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    refreshIntervalMs,
    isAuthenticated,
    hasLoaded,
    pauseWhenHidden,
    isTabVisible,
    loadActiveSessions,
  ]);

  // ============================================
  // Auto-load on mount and auth change
  // ============================================

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasLoaded) {
      loadActiveSessions(false);
    }

    if (!isAuthenticated) {
      setActiveSessionsState([]);
      setHasLoaded(false);
      lastRefreshRef.current = 0;
    }
  }, [autoLoad, isAuthenticated, hasLoaded, loadActiveSessions]);

  // ============================================
  // Context value
  // ============================================

  const contextValue = useMemo<ActiveParkingContextValue>(
    () => ({
      activeSessions,
      loading,
      hasActiveSessions,
      activeSessionCount,
      activeSessionForCar,
      isCarParked,
      startParking,
      updateParking,
      endParking,
      isStarting,
      isUpdating,
      isEnding,
      refresh,
      setActiveSessions,
      clearActiveSessions,
      lastError,
      clearError,
      refreshIntervalMs,
      isRefreshPaused,
    }),
    [
      activeSessions,
      loading,
      hasActiveSessions,
      activeSessionCount,
      activeSessionForCar,
      isCarParked,
      startParking,
      updateParking,
      endParking,
      isStarting,
      isUpdating,
      isEnding,
      refresh,
      setActiveSessions,
      clearActiveSessions,
      lastError,
      clearError,
      refreshIntervalMs,
      isRefreshPaused,
    ]
  );

  return (
    <ActiveParkingContext.Provider value={contextValue}>
      {children}
    </ActiveParkingContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Hook to access active parking sessions state from context.
 * Must be used within ActiveParkingProvider.
 *
 * @example
 * ```tsx
 * const {
 *   activeSessions,
 *   hasActiveSessions,
 *   activeSessionForCar,
 *   isCarParked,
 *   startParking,
 *   endParking,
 * } = useActiveParkingContext();
 *
 * // Check if a specific car is parked
 * if (isCarParked(carId)) {
 *   const session = activeSessionForCar(carId);
 *   // Show active parking indicator
 * }
 *
 * // Start parking
 * const session = await startParking({ carId, latitude: 51.05, longitude: -114.07, ... });
 *
 * // End parking
 * await endParking(session.id, { finalPrice: 5.00, currency: 'CAD' });
 * ```
 */
export const useActiveParkingContext = (): ActiveParkingContextValue => {
  const context = useContext(ActiveParkingContext);

  if (context === undefined) {
    throw new Error(
      "useActiveParkingContext must be used within an ActiveParkingProvider"
    );
  }

  return context;
};